import { useParams, Link as RouterLink } from "react-router";
import { useMutation, useQuery } from "@tanstack/react-query";
import { api, unwrap } from "../api.ts";
import { useMe } from "../hooks.ts";
import { Button, Card, ErrorText, Spinner } from "../components/ui.tsx";
import { Icon } from "../components/icons.tsx";

const NAME = { slack: "Slack", discord: "Discord", whatsapp: "WhatsApp" } as const;

/** Confirms that the chat account which asked for this link belongs to the signed-in user. */
export function LinkPage() {
  const { code = "" } = useParams();
  const me = useMe();
  const info = useQuery({ queryKey: ["link", code], queryFn: () => unwrap(api.me.messaging.link[":code"].$get({ param: { code } })), retry: false });
  const confirm = useMutation({ mutationFn: () => unwrap(api.me.messaging.link[":code"].$post({ param: { code } })) });

  return (
    <div className="mx-auto max-w-md pt-10">
      <Card className="p-8">
        {info.isPending ? (
          <div className="grid place-items-center py-8"><Spinner /></div>
        ) : info.error ? (
          <ErrorText error={info.error} />
        ) : confirm.isSuccess ? (
          <>
            <h1 className="flex items-center gap-2 text-xl font-semibold"><Icon name="checkCircle" size={22} className="text-emerald-600" /> Connected</h1>
            <p className="mt-2 text-sm text-[var(--muted)]">
              Your {NAME[info.data.platform]} account is now linked. Go back to {NAME[info.data.platform]} and message your assistant.
            </p>
            <RouterLink to="/connections/chat" className="mt-6 inline-block text-sm text-[var(--color-accent)] hover:underline">Manage chat apps</RouterLink>
          </>
        ) : (
          <>
            <h1 className="text-xl font-semibold">Connect {NAME[info.data.platform]}?</h1>
            <p className="mt-2 text-sm text-[var(--muted)]">
              The {NAME[info.data.platform]} account <b className="text-[var(--text)]">{info.data.displayName}</b> will talk to{" "}
              <b className="text-[var(--text)]">{me.data?.user.email}</b>'s assistant, with access to your files, connected apps and memories.
              Only continue if that {NAME[info.data.platform]} account is yours.
            </p>
            <ErrorText error={confirm.error} />
            <div className="mt-6 flex gap-2">
              <Button variant="primary" disabled={confirm.isPending} onClick={() => confirm.mutate()}>Yes, connect it</Button>
              <RouterLink to="/"><Button variant="ghost">Cancel</Button></RouterLink>
            </div>
          </>
        )}
      </Card>
    </div>
  );
}
